// Glanceable per-agent credit balance (spec §8). Self-hiding: free agents,
// unavailable reads, and flag-off all render nothing — the block looks
// exactly as it does today in those cases.
//
// Manage jumps to the Billing section of the one-scroll home (revised
// 2026-07-22: Billing is an anchored section, not a route) via the same
// onSelectAnchor callback every other anchor-nav control uses (VaultSidebar
// NavRow, ActivityFeed rows) — a button, not a Link, since there's no route
// to navigate to.

import { useCredits } from '@/src/hooks/useCredits';
import { formatCredits, NANOCREDITS_PER_CREDIT } from '@/src/lib/credits';

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function CreditChip({
  agentId, agentName, onSelectAnchor,
}: {
  agentId: string;
  agentName?: string;
  onSelectAnchor: (anchor: string) => void;
}) {
  const c = useCredits(agentId);
  if (c.status !== 'ready' || !c.hasRelationship || c.balance === null) return null;

  const low = c.granted !== null && !c.depleted && c.balance / c.granted <= 0.1;
  const cls = [
    'vault-credit-chip',
    c.depleted ? 'vault-credit-chip--depleted' : '',
    low ? 'vault-credit-chip--low' : '',
  ].filter(Boolean).join(' ');

  // useCredits already divides nc -> credits; recover nc (exact for these
  // magnitudes) since the shared formatter takes nanocredits.
  const balanceCr = formatCredits(c.balance * NANOCREDITS_PER_CREDIT);
  let label: string;
  if (c.subscription?.state === 'active' && c.granted !== null) {
    label = `${balanceCr} / ${formatCredits(c.granted * NANOCREDITS_PER_CREDIT)} cr${c.subscription.renewsAt ? ` · renews ${shortDate(c.subscription.renewsAt)}` : ''}`;
  } else if (c.subscription?.state === 'cancelled') {
    label = `${balanceCr} cr${c.subscription.burnsAt ? ` · until ${shortDate(c.subscription.burnsAt)}` : ''}`;
  } else {
    label = `${balanceCr} cr`;
  }

  return (
    <span className={cls}>
      <span className="vault-credit-chip__balance">{label}</span>
      <button
        type="button"
        className="vault-credit-chip__manage"
        onClick={() => onSelectAnchor('section-billing')}
        aria-label={agentName ? `Manage credits for ${agentName}` : 'Manage credits'}
      >
        Manage
      </button>
    </span>
  );
}
